import React from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  List, ListItem, ListItemIcon, ListItemText, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LicensePlan } from '@responsedock/config';
import { AppDialogTitle } from '../AppDialogTitle';
import { api } from '../../lib/api';
import {
  SALES_EMAIL, checkoutErrorCopy, openBillingPortal, startBillingCheckout, type BillingTier,
} from '../../lib/billing';
import {
  pickerPlans, planAha, planChangeSummary, planFootnotes, planPriceLabel, tierRank, type PlanChange, type WorkspacePlanContext,
} from '../../lib/planCatalog';
import { daysLeftLabel } from '../../lib/trial';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useWorkspaceUsage, workspaceUsageQueryKey, type WorkspaceUsage } from '../../hooks/useWorkspaceUsage';
import { useSurveys } from '../../hooks/useSurveys';
import { useAuthStore } from '../../store/auth';

interface PlanPickerDialogProps {
  open: boolean;
  highlightTier?: BillingTier | null;
  reason?: string | null;
  canManageBilling?: boolean;
  onClose: () => void;
}

interface CountedList { length: number }

function useOptionalList(key: 'webhooks' | 'api-keys', workspaceId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [key, workspaceId],
    queryFn: async () => {
      // Lists that a plan or verification gate blocks simply count as unknown here.
      const res = await api.get<Record<string, unknown>>(`/workspaces/${workspaceId}/${key}`, { skipGateDialog: true });
      const list = res.data[key === 'webhooks' ? 'webhooks' : 'keys'];
      return Array.isArray(list) ? list as Array<Record<string, unknown>> : [];
    },
    enabled: enabled && !!workspaceId,
    retry: false,
    staleTime: 30_000,
  });
}

/** The tier the picker preselects: the requested one, else the trial tier, else the next tier up. */
export function defaultPickerTier(usage: WorkspaceUsage | null | undefined, highlightTier?: BillingTier | null): BillingTier {
  if (highlightTier) return highlightTier;
  if (usage?.trial?.active) return usage.trial.tier;
  const current = usage?.tier ?? 'STARTER';
  if (current === 'STARTER') return 'PRO';
  if (current === 'PRO') return 'BUSINESS';
  return current;
}

function ChangeLine({ change }: { change: PlanChange }) {
  const icon = change.tone === 'gain'
    ? <AddCircleOutlineIcon fontSize="small" color="success" />
    : change.tone === 'loss'
      ? <RemoveCircleOutlineIcon fontSize="small" color="warning" />
      : <InfoOutlinedIcon fontSize="small" color="action" />;
  return (
    <ListItem disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
      <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>{icon}</ListItemIcon>
      <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={change.text} />
    </ListItem>
  );
}

/** In-app plan picker: compare plans, see what changes for this workspace, then go to checkout. */
export function PlanPickerDialog({ open, highlightTier, reason, canManageBilling = true, onClose }: PlanPickerDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const qc = useQueryClient();
  const { workspaceId, workspace } = useWorkspace();
  const isSystemAdmin = useAuthStore((s) => !!s.user?.isSystemAdmin);
  const usageQuery = useWorkspaceUsage();
  const usage = usageQuery.data ?? null;
  const surveysQuery = useSurveys(open ? workspaceId ?? '' : '', { pageSize: 1 });
  const webhooksQuery = useOptionalList('webhooks', workspaceId, open);
  const keysQuery = useOptionalList('api-keys', workspaceId, open);
  const [selected, setSelected] = React.useState<BillingTier>(() => defaultPickerTier(usage, highlightTier));
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<{ message: string; operatorDetail: string | null } | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setSelected(defaultPickerTier(usage, highlightTier));
    setError(null);
    setNotice(null);
    // Re-evaluate only when the picker opens or the request changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, highlightTier]);

  const trialActive = !!usage?.trial?.active;
  const currentTier: BillingTier = usage?.tier ?? 'STARTER';
  // The plan the workspace pays for: during a trial that is Starter until a plan is chosen.
  const paidTier: BillingTier = trialActive ? 'STARTER' : currentTier;
  const settings = workspace?.whiteLabelSettings ?? null;
  const context: WorkspacePlanContext = {
    currentTier,
    trialActive,
    responsesUsed: usage?.responsesUsed ?? 0,
    surveysCount: surveysQuery.data?.total ?? null,
    webhookCount: webhooksQuery.data ? (webhooksQuery.data as CountedList).length : null,
    publicKeyCount: keysQuery.data
      ? keysQuery.data.filter((key) => String(key['type']).toUpperCase() === 'PUBLIC' && !key['revokedAt']).length
      : null,
    brandingCustomised: !!(settings?.logoUrl || settings?.logo || settings?.primaryColor || workspace?.logoUrl || workspace?.primaryColor),
    hidesPlatformBranding: !!settings?.hidePlatformBranding,
    customDomainSet: !!settings?.customDomain,
    aiGenerationsUsed: usage?.aiGenerationsUsed ?? 0,
  };
  const plans = pickerPlans();
  const selectedPlan = plans.find((plan) => plan.tier === selected) ?? plans[0]!;
  const changes = planChangeSummary(selected, context);

  async function choose(plan: LicensePlan) {
    setError(null);
    setNotice(null);
    if (plan.tier === 'ENTERPRISE') {
      window.location.href = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`${plan.name} plan for ${workspace?.name ?? 'our workspace'}`)}`;
      return;
    }
    if (!workspaceId) return;
    if (plan.tier === 'STARTER') {
      if (trialActive) {
        setNotice(`Nothing to do: when your trial ends, ${workspace?.name ?? 'this workspace'} moves to ${plan.name} automatically. Nothing is deleted.`);
        return;
      }
      setPending(true);
      try {
        await openBillingPortal(workspaceId);
      } catch (cause) {
        setError(checkoutErrorCopy(cause, isSystemAdmin));
      } finally {
        setPending(false);
      }
      return;
    }
    setPending(true);
    try {
      const result = await startBillingCheckout(workspaceId, plan.tier);
      if (result.tierApplied) {
        await qc.invalidateQueries({ queryKey: workspaceUsageQueryKey(workspaceId) });
        await qc.invalidateQueries({ queryKey: ['billing-status', workspaceId] });
        setNotice(`${plan.name} is active for ${workspace?.name ?? 'this workspace'}.`);
      }
    } catch (cause) {
      setError(checkoutErrorCopy(cause, isSystemAdmin));
    } finally {
      setPending(false);
    }
  }

  function actionLabel(plan: LicensePlan): string {
    if (plan.tier === 'ENTERPRISE') return 'Talk to sales';
    if (plan.tier === paidTier && !trialActive) return 'Current plan';
    if (plan.tier === 'STARTER') return trialActive ? `Continue on ${plan.name}` : `Switch to ${plan.name}`;
    if (trialActive && plan.tier === currentTier) return `Keep ${plan.name}`;
    return tierRank(plan.tier) > tierRank(paidTier) ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`;
  }

  const selectedIsCurrent = selectedPlan.tier === paidTier && !trialActive;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen} scroll="paper" aria-labelledby="plan-picker-title">
      <AppDialogTitle id="plan-picker-title" onClose={onClose}>Choose a plan</AppDialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        {reason ? <Alert severity="info" sx={{ mb: 2 }}>{reason}</Alert> : null}
        {trialActive && usage?.trial ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            You&apos;re on a free {plans.find((plan) => plan.tier === usage.trial!.tier)?.name ?? 'Pro'} trial ({daysLeftLabel(usage.trial.daysLeft)}). Pick the plan you want to keep; checkout opens only after you choose.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Pick a plan to see exactly what changes for {workspace?.name ?? 'this workspace'}. Checkout opens only after you choose.
          </Typography>
        )}
        <Box
          role="radiogroup"
          aria-label="Plans"
          sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}
        >
          {plans.map((plan) => {
            const isSelected = plan.tier === selected;
            const isCurrent = plan.tier === paidTier && !trialActive;
            const isTrialTier = trialActive && plan.tier === currentTier;
            return (
              <Card
                key={plan.tier}
                variant="outlined"
                sx={{
                  borderWidth: 2,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                }}
              >
                <CardActionArea
                  role="radio"
                  aria-checked={isSelected}
                  data-testid={`plan-option-${plan.tier}`}
                  onClick={() => setSelected(plan.tier)}
                  sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                    <Typography fontWeight={800}>{plan.name}</Typography>
                    {isCurrent ? <Chip size="small" color="success" label="Current plan" />
                      : isTrialTier ? <Chip size="small" color="primary" label="On trial" />
                        : plan.badge ? <Chip size="small" variant="outlined" label={plan.badge} /> : null}
                  </Stack>
                  <Typography variant="h6" fontWeight={800}>{planPriceLabel(plan)}</Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.75}>{planAha(plan)}</Typography>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>

        <Box mt={2.5} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }} aria-live="polite">
          <Typography fontWeight={800} mb={0.5}>
            What changes with {selectedPlan.name}
          </Typography>
          {(surveysQuery.isLoading || usageQuery.isLoading) ? (
            <CircularProgress size={18} aria-label="Loading workspace usage" />
          ) : (
            <List dense disablePadding data-testid="plan-changes">
              {changes.map((change) => <ChangeLine key={change.text} change={change} />)}
            </List>
          )}
        </Box>

        {notice ? <Alert severity="success" sx={{ mt: 2 }}>{notice}</Alert> : null}
        {error ? (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="checkout-error">
            {error.message}
            {error.operatorDetail ? (
              <Typography variant="caption" display="block" mt={0.75} sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                Operator detail: {error.operatorDetail}
              </Typography>
            ) : null}
          </Alert>
        ) : null}
        {!canManageBilling ? (
          <Alert severity="info" sx={{ mt: 2 }}>Only workspace owners and admins can change the plan. Share this with one of them.</Alert>
        ) : null}
        {planFootnotes().length > 0 ? (
          <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2.25, color: 'text.secondary' }}>
            {planFootnotes().map((note) => <Typography key={note} component="li" variant="caption" display="list-item">{note}</Typography>)}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Not now</Button>
        <Button
          variant="contained"
          disabled={pending || !canManageBilling || selectedIsCurrent}
          onClick={() => void choose(selectedPlan)}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {pending ? 'Opening checkout…' : actionLabel(selectedPlan)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

